import { useMemo } from 'react';

import DataSheet, { type DataSheetColumn } from '@components/ui/DataSheet';

import { formatLabel } from './utils/pattern-resolver';
import type { TimeSeriesResponse } from './utils/pattern-resolver';
import { formatCount } from '@utils/currency';

interface TrendChartProps {
    name: string;
    response: TimeSeriesResponse;
}

type TrendRow = Record<string, string | number | null>;

const METRIC_LABELS: Record<string, string> = {
    planned_sales: 'Planned Sales',
    actual_sales: 'Actual Sales',
    opening_stock: 'Opening Stock',
    closing_stock: 'Closing Stock',
    in_transit: 'In Transit',
    consumption: 'Consumption',
    doh: 'Days on Hand',
    planned_shipment: 'Planned Shipment',
    actual_shipment: 'Actual Shipment',
};

const formatVal = (v: unknown): string => {
    if (v === null || v === undefined) return '-';
    const n = Number(v);
    if (isNaN(n)) return String(v);
    return Number.isInteger(n) ? formatCount(n) : n.toFixed(2);
};

export function TrendChart({ name, response }: TrendChartProps) {
    const { rows, columns } = useMemo(() => {
        if (!response.series || response.series.length === 0) return { rows: [], columns: [] as DataSheetColumn<TrendRow>[] };

        const metrics = response.series.map((s) => s.metric);
        const labels = response.series[0].data.map((d) => d.label);
        const valueMap = new Map(response.series.map((s) => [s.metric, s.data]));

        const dataRows: TrendRow[] = labels.map((label, i) => {
            const row: TrendRow = { label };
            for (const m of metrics) {
                row[m] = valueMap.get(m)?.[i]?.value ?? null;
            }
            return row;
        });

        const cols: DataSheetColumn<TrendRow>[] = [
            { key: 'label', header: 'Period', width: 120 },
            ...metrics.map((m) => ({
                key: m,
                header: METRIC_LABELS[m] ?? m,
                align: 'right' as const,
                render: (v: unknown) => formatVal(v),
            })),
        ];

        return { rows: dataRows, columns: cols };
    }, [response]);

    if (rows.length === 0) return null;

    return (
        <DataSheet
            title={formatLabel(name)}
            data={rows}
            columns={columns}
            maxHeight="auto"
        />
    );
}
